import { Modal, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { MSTContext } from "../../stores/main";
import { useHistory } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { BlingsBtn } from "../antd-extensions/blings-btn.component";
import { AsyncOpState } from "../../stores/async-op-state";
import { VidPart } from "../../API";
import SceneListItem from "./SceneListItem";
import "./SceneListSidebar.scss";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  AddPlusIcon,
  SidebarPanelLeftIcon,
  SidebarPanelRightIcon,
} from "../../icons-and-animations/Icons";
import confirmationModal from "../BlingsModal/CustomConfirm";
import { scenePath } from "../../utils/navigation";

type Props = {};

const MIN_WIDTH = 150;
const MAX_WIDTH = 270;
const DEFAULT_WIDTH = 170;
const COLLAPSED_WIDTH = 40; // Width when collapsed (just the toggle button)
const COMPACT_COLLAPSE_WIDTH = 1400;

const isCompactViewport = () => window.innerWidth < COMPACT_COLLAPSE_WIDTH;

export const SceneListSidebar = observer((props: Props) => {
  const {
    platformStore: {
      projectWorkspaceVersion,
      setVideoParts,
      selectedProjectId,
      selectedVideoPartName,
      deleteVideoPart,
      renameVideoPart,
      duplicateScene,
      createStarterScene,
      updateVideoPartDescription,
      setSelectedSceneName,
      hasUnsavedSceneChanges,
    },
    accountStore: { isBlingsAccount },
  } = useContext(MSTContext);
  const [modalUnsavedChanges, setModalUnsavedChanges] = useState("");
  const [allVideoPartsNames, setAllVideoPartsNames] = useState<string[]>([]);
  const [selectedVideoPartIndex, setSelectedVideoPartIndex] = useState(-1);
  const history = useHistory();
  const [activeId, setActiveId] = useState(null);
  const [isCreatingScene, setIsCreatingScene] = useState(false);

  // Sidebar resize and collapse state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("sceneSidebarWidth");
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sceneSidebarCollapsed");
    if (saved !== null) {
      return saved === "true";
    }

    return isCompactViewport();
  });
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(DEFAULT_WIDTH);
  const previousViewportWidthRef = useRef(window.innerWidth);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sceneListContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const items = Array.from(
        projectWorkspaceVersion?.videoParts || []
      ) as Array<VidPart>;
      const activeItemIndex = items.findIndex(
        (item) => item.name === active.id
      );
      const overItemIndex = items.findIndex((item) => item.name === over.id);
      const [reorderedItem] = items.splice(activeItemIndex, 1);
      items.splice(overItemIndex, 0, reorderedItem);
      setVideoParts(items);
    }
  };

  useEffect(() => {
    const selectedIndex = projectWorkspaceVersion?.videoParts
      ? projectWorkspaceVersion.videoParts.findIndex(
          (vp) => vp.name === selectedVideoPartName
        )
      : -1;
    setSelectedVideoPartIndex(selectedIndex);

    setAllVideoPartsNames(
      projectWorkspaceVersion?.videoParts?.map((vp) => vp.name) || []
    );
  }, [selectedVideoPartName, projectWorkspaceVersion?.videoParts]);

  useEffect(() => {
    if (isCollapsed || !selectedVideoPartName) {
      return;
    }

    const container = sceneListContainerRef.current;
    if (!container) {
      return;
    }

    const selectedSceneItem = Array.from(
      container.querySelectorAll<HTMLElement>("[data-scene-name]")
    ).find((item) => item.dataset.sceneName === selectedVideoPartName);

    selectedSceneItem?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [isCollapsed, selectedVideoPartName, projectWorkspaceVersion?.videoParts]);

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  const handleDeleteVideoPart = async (
    index: number,
    videoPartName: string
  ) => {
    if (
      projectWorkspaceVersion?.videoParts &&
      projectWorkspaceVersion?.videoParts?.length === 1
    ) {
      setSelectedVideoPartIndex(-1);
      return;
    }
    await deleteVideoPart(videoPartName);
    if (
      projectWorkspaceVersion?.videoParts &&
      projectWorkspaceVersion?.videoParts?.length === 0
    ) {
      setSelectedVideoPartIndex(-1);
      return;
    } else if (index === selectedVideoPartIndex) {
      const nextIndex =
        index > 0
          ? index - 1
          : projectWorkspaceVersion?.videoParts &&
            projectWorkspaceVersion?.videoParts?.length > 1
          ? index + 1
          : 0;
      const name = projectWorkspaceVersion?.videoParts?.[nextIndex].name;

      if (name) {
        setSelectedSceneName(name);
        history.push(scenePath(selectedProjectId || "", name));
      }
    }
  };

  const handleSceneClick = (sceneName: string) => {
    if (hasUnsavedSceneChanges) {
      setModalUnsavedChanges(sceneName);
    } else {
      setSelectedSceneName(sceneName);
      history.push(scenePath(selectedProjectId || "", sceneName));
    }
  };

  const handleCreateScene = async () => {
    if (isCreatingScene) {
      return;
    }

    if (hasUnsavedSceneChanges) {
      await confirmationModal.warning(
        "You have pending changes. Please save or discard them before adding a scene."
      );
      return;
    }

    setIsCreatingScene(true);
    let sceneName: string | undefined;
    try {
      sceneName = await createStarterScene();
    } catch (error) {
      console.error("Failed to create scene", error);
      await confirmationModal.warning(
        "The scene could not be created. Please try again."
      );
    } finally {
      setIsCreatingScene(false);
    }

    if (sceneName && selectedProjectId) {
      setSelectedSceneName(sceneName);
      history.push(scenePath(selectedProjectId || "", sceneName));
    }
  };

  // Resize handlers - using delta calculation pattern like TimelinePanel
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = sidebarWidth;
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    },
    [sidebarWidth]
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;

    const delta = e.clientX - startXRef.current;
    const newWidth = Math.max(
      MIN_WIDTH,
      Math.min(MAX_WIDTH, startWidthRef.current + delta)
    );
    setSidebarWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isResizingRef.current) {
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
  }, []);

  // Save width to localStorage whenever it changes (debounced via mouse up)
  useEffect(() => {
    if (!isResizingRef.current) {
      localStorage.setItem("sceneSidebarWidth", sidebarWidth.toString());
    }
  }, [sidebarWidth]);

  // Toggle collapse
  const toggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem("sceneSidebarCollapsed", newCollapsed.toString());
  };

  useEffect(() => {
    const handleViewportResize = () => {
      const viewportWidth = window.innerWidth;
      const isShrinking = viewportWidth < previousViewportWidthRef.current;

      if (isShrinking && viewportWidth < COMPACT_COLLAPSE_WIDTH) {
        setIsCollapsed(true);
      }

      previousViewportWidthRef.current = viewportWidth;
    };

    window.addEventListener("resize", handleViewportResize);

    return () => {
      window.removeEventListener("resize", handleViewportResize);
    };
  }, []);

  // Add mouse event listeners for resizing
  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={sidebarRef}
      className={`scene-list-sidebar ${isCollapsed ? "collapsed" : ""} ${
        isResizing ? "resizing" : ""
      }`}
      style={{
        width: isCollapsed ? COLLAPSED_WIDTH : sidebarWidth,
        minWidth: isCollapsed ? COLLAPSED_WIDTH : sidebarWidth,
        maxWidth: isCollapsed ? COLLAPSED_WIDTH : sidebarWidth,
      }}
    >
      <div className="scene-list-header">
        {!isCollapsed && <p className="scene-list-title">Scene list</p>}
        <div className="scene-list-header-actions">
          {!isCollapsed && (
            <Tooltip title="Add scene" placement="right">
              <button
                className={`scene-add-button ${
                  isCreatingScene ? "creating" : ""
                }`}
                onClick={handleCreateScene}
                aria-label="Add scene"
                disabled={isCreatingScene}
                type="button"
              >
                <AddPlusIcon />
              </button>
            </Tooltip>
          )}
          <Tooltip
            title={isCollapsed ? "Show scene list" : "Hide scene list"}
            placement="right"
          >
            <button
              className="sidebar-collapse-button"
              onClick={toggleCollapse}
              aria-label={isCollapsed ? "Show scene list" : "Hide scene list"}
              type="button"
            >
              {isCollapsed ? (
                <SidebarPanelRightIcon />
              ) : (
                <SidebarPanelLeftIcon />
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      {!isCollapsed && (
        <>
          <DndContext
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            collisionDetection={closestCenter}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
            sensors={sensors}
          >
            <div className="scene-list-container" ref={sceneListContainerRef}>
              {isCreatingScene && (
                <div
                  className="scene-create-feedback"
                  role="status"
                  aria-live="polite"
                >
                  <span
                    className="scene-create-feedback-dot"
                    aria-hidden="true"
                  />
                  <span>Creating scene...</span>
                </div>
              )}
              <SortableContext
                items={
                  projectWorkspaceVersion?.videoParts?.map((vp) => vp.name) ||
                  []
                }
                strategy={verticalListSortingStrategy}
              >
                {projectWorkspaceVersion?.videoParts &&
                  projectWorkspaceVersion.videoParts.map((vp, index) => (
                    <SceneListItem
                      vp={vp}
                      index={index}
                      key={vp.name}
                      selectedIndex={selectedVideoPartIndex}
                      selectedVideoPartName={selectedVideoPartName}
                      deleteVideoPart={() =>
                        handleDeleteVideoPart(index, vp.name)
                      }
                      handleClick={() => handleSceneClick(vp.name)}
                      renameVideoPart={renameVideoPart}
                      duplicateVideoPart={duplicateScene}
                      updateVideoPartDescription={updateVideoPartDescription}
                      allVideoPartsNames={allVideoPartsNames}
                      hasUnsavedChanges={hasUnsavedSceneChanges}
                      isBlingsAccount={isBlingsAccount}
                    />
                  ))}
              </SortableContext>
            </div>

            <DragOverlay modifiers={[restrictToParentElement]}>
              {activeId ? (
                <div className="scene-list-item dragging">
                  <p className="scene-name">{activeId}</p>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          <Modal
            className="unsaved-changes-modal"
            open={!!modalUnsavedChanges}
            onCancel={() => {
              setModalUnsavedChanges("");
            }}
            closable={false}
            footer={[
              <div
                key="unsaved-changes-actions"
                style={{
                  display: "flex",
                  gap: "15px",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <button
                  key="ok"
                  type="button"
                  onClick={() => {
                    setSelectedSceneName(modalUnsavedChanges);
                    history.push(
                      scenePath(selectedProjectId || "", modalUnsavedChanges)
                    );
                    setModalUnsavedChanges("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Proceed without saving
                </button>
                <BlingsBtn
                  key="publish"
                  className={"publish"}
                  opState={AsyncOpState.Changed}
                  htmlType={"submit"}
                  btnTexts={{
                    [AsyncOpState.Changed]: "Cancel",
                  }}
                  onClick={() => setModalUnsavedChanges("")}
                />
              </div>,
            ]}
          >
            <div className="popup-text">
              You have pending scene changes. Leaving this page will discard
              these changes
            </div>
          </Modal>

          {/* Resize handle */}
          <div
            className="sidebar-resize-handle"
            onMouseDown={handleMouseDown}
            style={{ display: isCollapsed ? "none" : "block" }}
          />
        </>
      )}
    </div>
  );
});
